import { h, Fragment } from 'preact'
import { useEffect } from 'preact/hooks'
import { createPortal } from 'preact/compat'

/**
 * ModalShell — Bootstrap-looking modal overlay for Preact chat modals.
 *
 * Renders into document.body via portal. Dismiss on backdrop click or Esc.
 *
 * Props: { open, onClose, title, iconClass?, children, footer?, sizeClass? }
 */
export function ModalShell({
  open,
  onClose,
  title,
  iconClass = 'bi-people',
  children,
  footer = null,
  dialogClass = 'modal-dialog-centered',
}) {
  useEffect(() => {
    if (!open) return undefined
    function onKey(e) {
      if (e.key === 'Escape') onClose?.()
    }
    document.addEventListener('keydown', onKey)
    document.body.classList.add('modal-open')
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.classList.remove('modal-open')
    }
  }, [open]) // eslint-disable-line

  if (!open) return null

  const modal = (
    <>
      <div
        class="modal fade show"
        style={{ display: 'block' }}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose?.()
        }}
      >
        <div class={`modal-dialog ${dialogClass}`} onClick={e => e.stopPropagation()}>
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">
                <i class={`bi ${iconClass} me-2`} />
                {title}
              </h5>
              <button
                type="button"
                class="btn-close"
                aria-label="Close"
                onClick={onClose}
              />
            </div>
            <div class="modal-body">{children}</div>
            {footer && <div class="modal-footer">{footer}</div>}
          </div>
        </div>
      </div>
      <div class="modal-backdrop fade show" onClick={onClose} />
    </>
  )

  return createPortal(modal, document.body)
}
